/** Optional harbour puzzles. Pure deterministic data and replay validation only.
 * No clock, save, voyage, wallet or DOM dependency. The player-session boundary
 * decides whether the occupied harbour/equipment allows a first-clear reward.
 */
export const HARBOUR_GAME_KINDS = ['line-mending', 'harbour-signals', 'tidal-pilot'] as const;
export type HarbourGameKind = typeof HARBOUR_GAME_KINDS[number];
export type HarbourGameDifficulty = 'basic' | 'advanced';
export const HARBOUR_GAME_LABELS: Readonly<Record<HarbourGameKind, string>> = {
  'line-mending': 'Line mending', 'harbour-signals': 'Harbour signals', 'tidal-pilot': 'Tidal pilot',
};
export const HARBOUR_GAME_EQUIPMENT: Readonly<Record<HarbourGameKind, { readonly id: string; readonly label: string }>> = {
  'line-mending': { id: 'rigging-kit', label: 'Rigging kit' },
  'harbour-signals': { id: 'signal-book', label: 'Signal book' },
  'tidal-pilot': { id: 'pilot-tools', label: 'Pilot tools' },
};
export const SIGNAL_SYMBOLS = [
  { glyph: '●', name: 'Circle' }, { glyph: '▲', name: 'Triangle' },
  { glyph: '■', name: 'Square' }, { glyph: '◆', name: 'Diamond' },
] as const;
interface PuzzleBase {
  readonly version: 1;
  readonly harbourId: string;
  readonly difficulty: HarbourGameDifficulty;
}
export interface NetPuzzle extends PuzzleBase {
  readonly kind: 'line-mending'; readonly size: 3 | 4; readonly damaged: readonly boolean[];
}
export interface SignalPuzzle extends PuzzleBase {
  readonly kind: 'harbour-signals'; readonly sequence: readonly number[];
}
export interface PilotPuzzle extends PuzzleBase {
  readonly kind: 'tidal-pilot'; readonly size: 5 | 6; readonly start: number; readonly quay: number;
  readonly shoals: readonly number[];
}
export type HarbourPuzzle = NetPuzzle | SignalPuzzle | PilotPuzzle;
export interface HarbourGameProof {
  readonly version: 1;
  readonly harbourId: string;
  readonly kind: HarbourGameKind;
  readonly difficulty: HarbourGameDifficulty;
  /** Net: selected patches. Signals: recalled symbols. Pilot: full cell path including start. */
  readonly moves: readonly number[];
}
export type HarbourGameValidation =
  | { readonly ok: true; readonly proof: HarbourGameProof; readonly clearKey: string }
  | { readonly ok: false; readonly reason: string };
const validHarbour = (id: unknown): id is string => typeof id === 'string' && /^place-(?:0\d|1\d)$/.test(id);
const validKind = (kind: unknown): kind is HarbourGameKind => HARBOUR_GAME_KINDS.includes(kind as HarbourGameKind);
const validDifficulty = (value: unknown): value is HarbourGameDifficulty => value === 'basic' || value === 'advanced';
function hash(text: string): number {
  let value = 2166136261;
  for (let i = 0; i < text.length; i++) value = Math.imul(value ^ text.charCodeAt(i), 16777619);
  return value >>> 0;
}
function randomFor(key: string): () => number {
  let state = hash(key);
  return () => {
    state = (state + 0x6D2B79F5) >>> 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}
export function harbourGameClearKey(harbourId: string, kind: HarbourGameKind, difficulty: HarbourGameDifficulty): string {
  if (!validHarbour(harbourId) || !validKind(kind) || !validDifficulty(difficulty)) throw new Error('Unknown harbour challenge');
  return `${harbourId}:${kind}:${difficulty}`;
}
/** Selected patch and its orthogonal neighbours: no wrap between row ends. */
export function netNeighbors(size: number, cell: number): number[] {
  const x = cell % size, y = Math.floor(cell / size), result = [cell];
  if (x > 0) result.push(cell - 1);
  if (x + 1 < size) result.push(cell + 1);
  if (y > 0) result.push(cell - size);
  if (y + 1 < size) result.push(cell + size);
  return result;
}
export function netStateAfter(puzzle: NetPuzzle, moves: readonly number[]): boolean[] {
  const state = [...puzzle.damaged];
  for (const move of moves) for (const cell of netNeighbors(puzzle.size, move)) state[cell] = !state[cell];
  return state;
}
export function createHarbourPuzzle(harbourId: string, kind: HarbourGameKind, difficulty: HarbourGameDifficulty): HarbourPuzzle {
  const key = harbourGameClearKey(harbourId, kind, difficulty), random = randomFor(`harbour-games-v1:${key}`);
  const base = { version: 1 as const, harbourId, difficulty };
  if (kind === 'line-mending') {
    const size = difficulty === 'basic' ? 3 : 4, damaged = Array<boolean>(size * size).fill(false);
    const selected = new Set<number>();
    while (selected.size < (difficulty === 'basic' ? 4 : 8)) selected.add(Math.floor(random() * size * size));
    for (const move of selected) for (const cell of netNeighbors(size, move)) damaged[cell] = !damaged[cell];
    if (!damaged.some(Boolean)) for (const cell of netNeighbors(size, 0)) damaged[cell] = true;
    return { ...base, kind, size, damaged };
  }
  if (kind === 'harbour-signals') {
    const sequence = Array.from({ length: difficulty === 'basic' ? 4 : 6 }, () => Math.floor(random() * SIGNAL_SYMBOLS.length));
    return { ...base, kind, sequence };
  }
  const size = difficulty === 'basic' ? 5 : 6, start = size * (size - 1), quay = size - 1;
  // Reserve a winding monotone course first, then place shoals elsewhere. A valid
  // route exists by construction; neither this reserved course nor a solution is exported.
  const water = new Set([start]);
  let x = 0, y = size - 1;
  while (x < size - 1 || y > 0) {
    if (x === size - 1 || (y > 0 && random() < 0.5)) y--; else x++;
    water.add(y * size + x);
  }
  const shoals: number[] = [];
  for (let cell = 0; cell < size * size; cell++) if (!water.has(cell) && random() < (difficulty === 'basic' ? 0.55 : 0.7)) shoals.push(cell);
  if (shoals.length === 0) for (let cell = 0; cell < size * size; cell++) if (!water.has(cell)) { shoals.push(cell); break; }
  return { ...base, kind, size, start, quay, shoals };
}

/** Replay the complete evidence against the deterministic puzzle. Client state,
 * success flags, reward amounts and supplied puzzle boards are never trusted.
 */
export function validateHarbourGameProof(input: unknown): HarbourGameValidation {
  const no = (reason: string): HarbourGameValidation => ({ ok: false, reason });
  if (typeof input !== 'object' || input === null || Array.isArray(input)) return no('Invalid challenge proof.');
  const p = input as Record<string, unknown>;
  if (p['version'] !== 1 || !validHarbour(p['harbourId']) || !validKind(p['kind']) || !validDifficulty(p['difficulty'])) return no('Unknown harbour challenge.');
  if (!Array.isArray(p['moves']) || p['moves'].length === 0 || p['moves'].length > 256 ||
    !Array.from(p['moves']).every(m => typeof m === 'number' && Number.isSafeInteger(m) && m >= 0)) return no('Invalid challenge moves.');
  const moves = [...p['moves']] as number[];
  const puzzle = createHarbourPuzzle(p['harbourId'], p['kind'], p['difficulty']);
  if (puzzle.kind === 'line-mending') {
    if (moves.some(m => m >= puzzle.size * puzzle.size)) return no('That patch is outside the net.');
    if (netStateAfter(puzzle, moves).some(Boolean)) return no('Some patches are still damaged. Keep mending.');
  } else if (puzzle.kind === 'harbour-signals') {
    if (moves.length !== puzzle.sequence.length || moves.some((m, i) => m !== puzzle.sequence[i])) return no('That signal order does not match. Study the sequence and try again.');
  } else {
    if (moves.length > puzzle.size * puzzle.size || moves[0] !== puzzle.start || moves[moves.length - 1] !== puzzle.quay || new Set(moves).size !== moves.length) return no('Plot one continuous course from Start to Quay.');
    const shoals = new Set(puzzle.shoals);
    for (let i = 0; i < moves.length; i++) {
      const cell = moves[i]!;
      if (cell >= puzzle.size * puzzle.size || shoals.has(cell)) return no('The course crosses a shoal.');
      if (i > 0) {
        const previous = moves[i - 1]!;
        const gap = Math.abs(cell % puzzle.size - previous % puzzle.size) + Math.abs(Math.floor(cell / puzzle.size) - Math.floor(previous / puzzle.size));
        if (gap !== 1) return no('Each course step must reach a neighboring water square.');
      }
    }
  }
  const proof: HarbourGameProof = { version: 1, harbourId: puzzle.harbourId, kind: puzzle.kind, difficulty: puzzle.difficulty, moves };
  return { ok: true, proof, clearKey: harbourGameClearKey(proof.harbourId, proof.kind, proof.difficulty) };
}
